// Shared option sets + source builders used by the master listing "smart filters".

export const STATUS_FILTER = {
  key: "is_active",
  label: "Status",
  width: 150,
  options: [
    { label: "Active", value: true },
    { label: "In-active", value: false },
  ],
};

export const YES_NO_OPTIONS = [
  { label: "Yes", value: true },
  { label: "No", value: false },
];

export const CATEGORY_TYPE_OPTIONS = [
  { label: "Material", value: 0 },
  { label: "Sparepart", value: 1 },
];

export const INTAKE_STATUS_OPTIONS = [
  { label: "Pending", value: "Pending" },
  { label: "Pending Approval", value: "Pending Approval" },
  { label: "Repair Completed", value: "Repair Completed" },
  { label: "Scrap", value: "Scrap" },
  { label: "Scrap Approved", value: "Scrap Approved" },
  { label: "Dispatched", value: "Dispatched" },
];

// ---- lookup driven filters (options come from the related master) ----

const lookup = (key, label, modelName, labelField, extra = {}) => ({
  key,
  label,
  width: 180,
  source: {
    modelName,
    labelField,
    valueField: "id",
    whereCondition: { is_active: true },
    orderBy: { field: labelField, order: "ASC" },
  },
  ...extra,
});

export const COUNTRY_FILTER = lookup(
  "country_id",
  "Country",
  "country_masters",
  "country_name"
);

export const STATE_FILTER = lookup(
  "state_id",
  "State",
  "state_masters",
  "state_name",
  { dependsOn: "country_id", parentField: "country_id" }
);

export const CITY_FILTER = lookup("city_id", "City", "city_masters", "city_name", {
  dependsOn: "state_id",
  parentField: "state_id",
});

export const BRANCH_FILTER = lookup(
  "branch_id",
  "Branch",
  "branch_masters",
  "branch_name"
);

// self-referencing: filters the Branch list itself by its own branch_name column
// (valueField is branch_name, not id, since the filter applies directly on this model)
export const BRANCH_NAME_FILTER = {
  key: "branch_name",
  label: "Branch Name",
  width: 180,
  source: {
    modelName: "branch_masters",
    labelField: "branch_name",
    valueField: "branch_name",
    whereCondition: { is_active: true },
    orderBy: { field: "branch_name", order: "ASC" },
  },
};

export const ROLE_FILTER = lookup("role_id", "Role", "role_masters", "role_name");

export const GROUP_FILTER = lookup("group_id", "Group", "group_master", "name");

export const OEM_FILTER = lookup("oem_id", "OEM", "oem_master", "name");

export const UOM_FILTER = lookup("uom_id", "UOM", "uom_masters", "name");

export const CATEGORY_FILTER = lookup(
  "category_id",
  "Category",
  "category_masters",
  "name"
);

export const CLIENT_FILTER = lookup(
  "customer_id",
  "Customer",
  "client_masters",
  "client_name"
);

export const ZONE_FILTER = lookup("zone_id", "Zone", "zone_masters", "zone_name");

export const ITEM_FILTER = lookup(
  "material_id",
  "GMDS Code",
  "material_masters",
  "gmds_code"
);

// self-referencing: filters the Item list itself by its own gmds_code column
export const ITEM_GMDS_CODE_FILTER = {
  key: "gmds_code",
  label: "GMDS Code",
  width: 180,
  source: {
    modelName: "material_masters",
    labelField: "gmds_code",
    valueField: "gmds_code",
    whereCondition: { is_active: true },
    orderBy: { field: "gmds_code", order: "ASC" },
  },
};

// self-referencing: filters the Item list itself by its own descriptions column
// (free-text field picked from a dropdown of existing values, same limitation as
// ITEM_GMDS_CODE_FILTER/BRANCH_NAME_FILTER - no substring/LIKE filter type exists yet)
export const ITEM_DESCRIPTION_FILTER = {
  key: "descriptions",
  label: "Item Description",
  width: 200,
  source: {
    modelName: "material_masters",
    labelField: "descriptions",
    valueField: "descriptions",
    whereCondition: { is_active: true },
    orderBy: { field: "descriptions", order: "ASC" },
  },
};

// user_masters.full_name is a VIRTUAL column, so the list has to be ordered by a
// real one - everything else is identical to `lookup()`.
const userLookup = (key, label, whereCondition = { is_active: true }) => ({
  key,
  label,
  width: 180,
  source: {
    modelName: "user_masters",
    labelField: "full_name",
    valueField: "id",
    whereCondition,
    orderBy: { field: "first_name", order: "ASC" },
  },
});

// role_id 2 = Engineer / Technician (same condition the create screens use)
export const ENGINEER_FILTER = userLookup("technician_id", "Engineer", {
  is_active: true,
  role_id: 2,
});

export const DISPATCHED_BY_FILTER = userLookup("dispatched_by", "Dispatched By");

// Dispatched list: filters by fields that live on related models (material_intakes /
// material_masters, reached via the "intake"/"material" includes already present on
// the list query), so they need a custom buildWhere using Sequelize's `$assoc.column$`
// nested-where syntax instead of a plain top-level column match.
export const DISPATCHED_SERIAL_NO_FILTER = {
  key: "material_serial_no",
  label: "Serial No",
  width: 180,
  source: {
    modelName: "material_intakes",
    labelField: "material_serial_no",
    valueField: "material_serial_no",
    whereCondition: { is_active: true },
    orderBy: { field: "material_serial_no", order: "ASC" },
  },
  buildWhere: (selected) => ({ "$intake.material_serial_no$": selected.value }),
};

export const DISPATCHED_GMDS_CODE_FILTER = {
  key: "dispatched_gmds_code",
  label: "GMDS Code",
  width: 180,
  source: {
    modelName: "material_masters",
    labelField: "gmds_code",
    valueField: "gmds_code",
    whereCondition: { is_active: true },
    orderBy: { field: "gmds_code", order: "ASC" },
  },
  buildWhere: (selected) => ({ "$intake.material.gmds_code$": selected.value }),
};

// Spare Part Inventory list: gmds_code/descriptions live on material_masters,
// reached via the "material" include already present on this list's query -
// same one-hop nested-where pattern as DISPATCHED_GMDS_CODE_FILTER above.
export const SPARE_INVENTORY_GMDS_CODE_FILTER = {
  key: "spare_inventory_gmds_code",
  label: "GMDS Code",
  width: 180,
  source: {
    modelName: "material_masters",
    labelField: "gmds_code",
    valueField: "gmds_code",
    whereCondition: { is_active: true },
    orderBy: { field: "gmds_code", order: "ASC" },
  },
  buildWhere: (selected) => ({ "$material.gmds_code$": selected.value }),
};

// label is "Item Name" per the list's column heading, but the underlying
// field is still material_masters.descriptions
export const SPARE_INVENTORY_ITEM_DESCRIPTION_FILTER = {
  key: "spare_inventory_item_description",
  label: "Item Name",
  width: 200,
  source: {
    modelName: "material_masters",
    labelField: "descriptions",
    valueField: "descriptions",
    whereCondition: { is_active: true },
    orderBy: { field: "descriptions", order: "ASC" },
  },
  buildWhere: (selected) => ({ "$material.descriptions$": selected.value }),
};

// item_location lives directly on inverts itself - a plain self-referencing
// filter, same pattern as BRANCH_NAME_FILTER.
export const SPARE_INVENTORY_ITEM_LOCATION_FILTER = {
  key: "item_location",
  label: "Location",
  width: 180,
  source: {
    modelName: "inverts",
    labelField: "item_location",
    valueField: "item_location",
    whereCondition: { is_active: true },
    orderBy: { field: "item_location", order: "ASC" },
  },
};

// inverts.created_by is a direct column - plain FK lookup, same pattern as
// DISPATCHED_BY_FILTER/RECEIVED_BY_FILTER.
export const SPARE_INVENTORY_CREATED_BY_FILTER = userLookup(
  "created_by",
  "Created By"
);

// Engineer Inventory list: gmds_code/descriptions live on material_masters,
// reached via the "material" include already present on this list's query.
// Both use nested-where (rather than the existing ITEM_FILTER's plain
// material_id equality) so GMDS Code and Item Description can coexist as
// independent filters without both trying to own the same "material_id" key.
export const TECHNICIAN_INVENTORY_GMDS_CODE_FILTER = {
  key: "technician_inventory_gmds_code",
  label: "GMDS Code",
  width: 180,
  source: {
    modelName: "material_masters",
    labelField: "gmds_code",
    valueField: "gmds_code",
    whereCondition: { is_active: true },
    orderBy: { field: "gmds_code", order: "ASC" },
  },
  buildWhere: (selected) => ({ "$material.gmds_code$": selected.value }),
};

export const TECHNICIAN_INVENTORY_ITEM_DESCRIPTION_FILTER = {
  key: "technician_inventory_item_description",
  label: "Item Description",
  width: 200,
  source: {
    modelName: "material_masters",
    labelField: "descriptions",
    valueField: "descriptions",
    whereCondition: { is_active: true },
    orderBy: { field: "descriptions", order: "ASC" },
  },
  buildWhere: (selected) => ({ "$material.descriptions$": selected.value }),
};

// Engineer Stock Return list: same nested-where pattern as the Engineer
// Inventory filters above - gmds_code/descriptions live on material_masters,
// reached via the "material" include already present on this list's query.
export const STOCK_RETURN_GMDS_CODE_FILTER = {
  key: "stock_return_gmds_code",
  label: "GMDS Code",
  width: 180,
  source: {
    modelName: "material_masters",
    labelField: "gmds_code",
    valueField: "gmds_code",
    whereCondition: { is_active: true },
    orderBy: { field: "gmds_code", order: "ASC" },
  },
  buildWhere: (selected) => ({ "$material.gmds_code$": selected.value }),
};

export const STOCK_RETURN_ITEM_DESCRIPTION_FILTER = {
  key: "stock_return_item_description",
  label: "Item Description",
  width: 200,
  source: {
    modelName: "material_masters",
    labelField: "descriptions",
    valueField: "descriptions",
    whereCondition: { is_active: true },
    orderBy: { field: "descriptions", order: "ASC" },
  },
  buildWhere: (selected) => ({ "$material.descriptions$": selected.value }),
};

// Scrap list: descriptions lives on material_masters, reached via the
// "intake" -> "material" include already present on this list's query -
// same two-hop nested-where pattern as DISPATCHED_GMDS_CODE_FILTER.
export const SCRAP_ITEM_DESCRIPTION_FILTER = {
  key: "scrap_item_description",
  label: "Item Description",
  width: 200,
  source: {
    modelName: "material_masters",
    labelField: "descriptions",
    valueField: "descriptions",
    whereCondition: { is_active: true },
    orderBy: { field: "descriptions", order: "ASC" },
  },
  buildWhere: (selected) => ({ "$intake.material.descriptions$": selected.value }),
};

// scraps.created_by is a direct column - plain FK lookup, same pattern as
// SPARE_INVENTORY_CREATED_BY_FILTER.
export const SCRAP_BY_FILTER = userLookup("created_by", "Scrap By");

// same two-hop nested-where pattern as SCRAP_ITEM_DESCRIPTION_FILTER above.
export const SCRAP_GMDS_CODE_FILTER = {
  key: "scrap_gmds_code",
  label: "GMDS Code",
  width: 180,
  source: {
    modelName: "material_masters",
    labelField: "gmds_code",
    valueField: "gmds_code",
    whereCondition: { is_active: true },
    orderBy: { field: "gmds_code", order: "ASC" },
  },
  buildWhere: (selected) => ({ "$intake.material.gmds_code$": selected.value }),
};

// scraps.status is a free-text workflow field (e.g. "Pending Approval",
// "Scrapped") - self-referencing dropdown so new values stay filterable
// without a code change, same pattern as BRANCH_NAME_FILTER.
export const SCRAP_STATUS_FILTER = {
  key: "status",
  label: "Status",
  width: 180,
  source: {
    modelName: "scraps",
    labelField: "status",
    valueField: "status",
    whereCondition: { is_active: true },
    orderBy: { field: "status", order: "ASC" },
  },
};

// item_location lives directly on technician_stock_returns - a plain
// self-referencing filter, same pattern as SPARE_INVENTORY_ITEM_LOCATION_FILTER.
export const STOCK_RETURN_ITEM_LOCATION_FILTER = {
  key: "stock_return_item_location",
  label: "Item Location",
  width: 180,
  source: {
    modelName: "technician_stock_returns",
    labelField: "item_location",
    valueField: "item_location",
    whereCondition: { is_active: true },
    orderBy: { field: "item_location", order: "ASC" },
  },
};

export const PRIORITY_ONE_ENGINEER_FILTER = userLookup(
  "priority_1_user_id",
  "Priority 1 Engineer"
);

export const PRIORITY_TWO_ENGINEER_FILTER = userLookup(
  "priority_2_user_id",
  "Priority 2 Engineer"
);

export const PRIORITY_THREE_ENGINEER_FILTER = userLookup(
  "priority_3_user_id",
  "Priority 3 Engineer"
);

export const RECEIVED_BY_FILTER = userLookup("received_by", "Received By");

// Outgoing Invoice list: `dispatch_ids` is a plain JSONB array of dispatch ids
// on outgoing_invoices (no real FK/association back from dispatcheds), so this
// can't be a simple column match - the dropdown is sourced from `dispatcheds`
// (labelled by the linked intake's serial number, valued by the dispatch id),
// and the where clause checks JSONB array containment instead of equality.
export const OUTGOING_INVOICE_SERIAL_NO_FILTER = {
  key: "dispatch_ids",
  label: "Serial No",
  width: 180,
  source: {
    modelName: "dispatcheds",
    labelField: "intake.material_serial_no",
    valueField: "id",
    whereCondition: { is_active: true },
    include: [{ model: "material_intakes", as: "intake" }],
    orderBy: { field: "id", order: "ASC" },
  },
  buildWhere: (selected) => ({ dispatch_ids: { contains: [selected.value] } }),
};

// courier_docket_number lives directly on outgoing_invoices - a plain
// self-referencing filter, same pattern as BRANCH_NAME_FILTER.
export const COURIER_DOCKET_NUMBER_FILTER = {
  key: "courier_docket_number",
  label: "Courier Docket Number",
  width: 200,
  source: {
    modelName: "outgoing_invoices",
    labelField: "courier_docket_number",
    valueField: "courier_docket_number",
    whereCondition: { is_active: true, status: 2 },
    orderBy: { field: "courier_docket_number", order: "ASC" },
  },
};

export const INVOICE_COURIER_STATUS_OPTIONS = [
  { label: "Courier Pending", value: 1 },
  { label: "Courier Completed", value: 2 },
];
